import type { TokenCredential } from "@azure/core-auth";
import { signAppJwt } from "./jwt.js";
import { readAppSecrets } from "./secrets.js";
import { getCachedToken, putCachedToken } from "./cache.js";

export interface MintedToken {
  token: string;
  expiresAt: Date;
  permissions: Record<string, string>;
  repositorySelection: "all" | "selected";
}

/**
 * Mint a GitHub App installation access token, optionally narrowed to a
 * single repository. Caches the result keyed by (installationId, repository)
 * with a 10-min safety margin before expiry; subsequent calls return the
 * cached value until that window closes.
 *
 * @throws on any non-2xx GitHub response; the error message carries the
 *         status code and response body so the operator can diagnose
 *         (typical: 404 = wrong installation id; 401 = bad/expired App JWT).
 */
export async function mintInstallationToken(args: {
  credential: TokenCredential;
  kvUri: string;
  installationId: string;
  repository: string;
  /** Least-privilege scope: when provided, the token is narrowed to exactly these
   *  permissions (e.g. `{ contents: "write", pull_requests: "write" }`). GitHub
   *  refuses grants that exceed the installation's configured scope, so callers
   *  should only pass the permissions they actually need (S4 invariant). */
  permissions?: Record<string, string>;
}): Promise<MintedToken> {
  const cached = getCachedToken(
    args.installationId,
    args.repository,
    args.permissions,
  );
  if (cached) {
    return {
      token: cached.token,
      expiresAt: cached.expiresAt,
      permissions: cached.permissions ?? { ...(args.permissions ?? {}) },
      repositorySelection: cached.repositorySelection ?? "selected",
    };
  }

  const { appId, privateKeyPem } = await readAppSecrets({
    credential: args.credential,
    kvUri: args.kvUri,
  });
  const appJwt = signAppJwt({ appId, privateKeyPem });

  const url = `https://api.github.com/app/installations/${args.installationId}/access_tokens`;
  // GitHub expects the bare repo name (no owner/) in `repositories` — sending
  // "owner/name" 422s with "not accessible" as soon as the installation has
  // more than one repo (silently worked single-repo installations because
  // GitHub matched leniently). Callers pass "owner/name" for cache-key uniqueness
  // across owners; we strip it here.
  const repoName = args.repository.includes("/")
    ? args.repository.split("/", 2)[1]
    : args.repository;
  const body = JSON.stringify({
    repositories: [repoName],
    ...(args.permissions ? { permissions: args.permissions } : {}),
  });
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${appJwt}`,
      Accept: "application/vnd.github+json",
      "X-GitHub-Api-Version": "2022-11-28",
      "Content-Type": "application/json",
    },
    body,
  });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(
      `mintInstallationToken: HTTP ${String(res.status)} from ${url}\n${text.slice(0, 500)}`,
    );
  }

  const parsed = JSON.parse(text) as {
    token: string;
    expires_at: string;
    permissions: Record<string, string>;
    repository_selection: "all" | "selected";
  };
  const expiresAt = new Date(parsed.expires_at);
  putCachedToken(
    args.installationId,
    args.repository,
    parsed.token,
    expiresAt,
    args.permissions,
    {
      permissions: parsed.permissions,
      repositorySelection: parsed.repository_selection,
    },
  );
  return {
    token: parsed.token,
    expiresAt,
    permissions: parsed.permissions,
    repositorySelection: parsed.repository_selection,
  };
}
